import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { PAYMENT_FIELDS } from 'constants/formFields';
import { evAddAddressClick, evAddPaymentInstrument } from 'events/account';

import OrderCareBillingAddressCard from 'components/account/Order/OrderCareBillingAddressCard';
import OrderCareAddNewBillingAddress from 'components/account/Order/OrderCareAddNewBillingAddress';
import { SmallLoader } from 'components/Loader';
import {
  addNewPayment,
  fetchBillingAddresses,
  receiveAddNewPaymentError,
  toggleAddNewBillingAddress
} from 'actions/account/order';
import { makeFormErrorMessage } from 'helpers/MyAccountRenderUtils';
import { isValidCreditCardNumber } from 'helpers/PaymentUtils';
import { titaniteView, track } from 'apis/amethyst';
import css from 'styles/components/account/orderCareAddPayment.scss';

export class OrderCareAddPayment extends Component {
  state = {
    instrument: {
      name: null,
      cc: null,
      expirationMonth: null,
      expirationYear: null,
      paymentInstrumentId: null,
      isPrimary: false
    },
    addressId: '0'
  };

  componentDidMount() {
    titaniteView();

    const { fetchBillingAddresses } = this.props;
    fetchBillingAddresses();
  }

  onHandleFormStateChange = e => {
    const {
      currentTarget,
      currentTarget: { dataset: { field } } // form field updated
    } = e;

    const instrument = { ...this.state.instrument };
    if (field === 'currentMonth' || field === 'currentYear') {
      instrument[field] = currentTarget.options[currentTarget.selectedIndex].value;
    } else {
      instrument[field] = currentTarget.value;
    }
    this.setState({ instrument });
  }

  /*
    This is called twice for the 'Add New Payment' flow.
    (1) When initially submitting cc info to generate a new paymentInstrumentId
    (2) After customer selects a billing address which allows us to send an addressId
    The call will update from a post to a put for the 2nd case.
  */
  onSavePayment = e => {
    e.preventDefault();
    const { addNewPayment, receiveAddNewPaymentError } = this.props;
    const { instrument: { cc } } = this.state;
    if (!isValidCreditCardNumber(cc)) {
      const error = { extraInformation: [{ fieldName: 'addCreditCardNumber' }] };
      receiveAddNewPaymentError(error);
    } else {
      Promise.resolve(addNewPayment(this.state)).then(response => {
        if (response?.paymentInstrumentId) {
          const { paymentInstrumentId, isPrimarySuccess } = response;
          track(evAddPaymentInstrument, { valid: true, paymentInstrumentId, isPrimarySuccess });
        }
      });
    }
  }

  makeCCMonths() {
    const monthsArr = [];
    monthsArr.push(<option key="ccMonth_default" value="">Month</option>);
    for (let i = 1; i <= 12; i++) {
      monthsArr.push(<option key={`ccMonth_${i}`} value={i}>{i < 10 ? `0${i}` : i}</option>);
    }
    return monthsArr;
  }

  makeCCYears() {
    const yearsArr = [];
    const currYear = (new Date()).getFullYear();
    yearsArr.push(<option key="ccYear_default" value="">Year</option>);
    for (let i = currYear; i < currYear + 15; i++) {
      yearsArr.push(<option key={`ccYear_${i}`} value={i}>{i}</option>);
    }
    return yearsArr;
  }

  makeBillingAddressCards(addresses) {
    const addressCards = [];
    addresses.map(address => {
      const { addressId } = address;
      addressCards.push(<OrderCareBillingAddressCard
        key={addressId}
        address={address}
        onSelectBillingAddress={this.onSelectBillingAddress}
        isRequired={true} />);
    });
    return addressCards;
  }

  updateStatePaymentInstrumentId() {
    const { order: { newPaymentInstrumentId } } = this.props; // present after initial credit card input
    const instrument = { ...this.state.instrument };
    instrument.paymentInstrumentId = newPaymentInstrumentId;
    this.setState({ instrument });
  }

  onSelectBillingAddress = e => {
    this.updateStatePaymentInstrumentId();
    this.setState({ addressId: e.currentTarget.id });
  }

  onAddNewAddress = e => {
    e.preventDefault();
    const { toggleAddNewBillingAddress } = this.props;
    track(evAddAddressClick, { addressType: 1 }); // amethyst addressType 1 => billing
    toggleAddNewBillingAddress(true);
  }

  onCancelAddNewAddress = () => {
    const { toggleAddNewBillingAddress } = this.props;
    toggleAddNewBillingAddress(false);
  }

  render() {
    const {
      onCancelAddNewPayment,
      order: {
        billingAddressesVisible,
        addresses,
        isNewPaymentUpdating,
        newPaymentError,
        newPaymentErrorDetails,
        isPaymentUpdating, // on select billing address final step
        addNewBillingAddressVisible
      }
    } = this.props;
    const { instrument: { name, cc } } = this.state;
    const { testId } = this.context;

    if (isNewPaymentUpdating) {
      return <SmallLoader />;
    }
    if (addNewBillingAddressVisible) {
      return <OrderCareAddNewBillingAddress
        onCancelAddNewAddress={this.onCancelAddNewAddress}
        ccStateInstrumentData={this.state} />;
    }
    if (billingAddressesVisible && addresses.length) {
      return (
        <div className={css.addressCardsWrap}>
          <h2 data-test-id={testId('useExistingBillingAddressHeading')}>Choose a Billing Address</h2>
          <form method="post" onSubmit={this.onSavePayment}>
            <div className={css.addressCards}>
              {this.makeBillingAddressCards(addresses)}
            </div>
            <button
              type="submit"
              disabled={isPaymentUpdating}
              data-test-id={testId('useExistingBillingAddress')}>Use Address</button>
            <button type="button" onClick={this.onAddNewAddress} data-test-id={testId('addNewBillingAddress')}>Add New Address</button>
          </form>
        </div>
      );
    }
    return (
      <div className={css.newPaymentWrap}>
        {newPaymentError && <div className={css.errorMsg}>{makeFormErrorMessage(newPaymentErrorDetails)}</div>}
        <h2 data-test-id={testId('addNewPaymentHeading')}>New Payment Information</h2>
        <form method="post" onSubmit={this.onSavePayment}>
          <div className={css.nameAndCCWrap}>
            <p className={css.name}>
              <label htmlFor="addCreditCardName">Name on card</label>
              <input
                id="addCreditCardName"
                className={cn({ [css.inputError] : newPaymentErrorDetails })}
                onChange={this.onHandleFormStateChange}
                data-field={PAYMENT_FIELDS.NAME_ON_CARD.fieldName}
                type="text"
                placeholder="First and Last"
                value={name ? name : ''}
                maxLength={PAYMENT_FIELDS.NAME_ON_CARD.maxLength}
                required
                data-test-id={testId('ccNameOnCard')} />
            </p>
            <p className={css.ccNum}>
              <label htmlFor="addCreditCardNumber">Card number</label>
              <input
                id="addCreditCardNumber"
                className={cn({ [css.inputError] : newPaymentErrorDetails })}
                onChange={this.onHandleFormStateChange}
                data-field={PAYMENT_FIELDS.CC.fieldName}
                type="text"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={cc ? cc : ''}
                maxLength={PAYMENT_FIELDS.CC.maxLength}
                required
                data-test-id={testId('ccNumber')} />
            </p>
          </div>
          <p className={css.expirationDate}>
            <span>Expiration date:</span>
            <select
              className={cn({ [css.inputError] : newPaymentErrorDetails })}
              onChange={this.onHandleFormStateChange}
              data-field={PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName}
              defaultValue="ccMonth_default"
              required
              data-test-id={testId('expirationMonth')}>{this.makeCCMonths()}</select>
            <select
              className={cn({ [css.inputError] : newPaymentErrorDetails })}
              onChange={this.onHandleFormStateChange}
              data-field={PAYMENT_FIELDS.CC_EXPIRATION_YEAR.fieldName}
              defaultValue="ccYear_default"
              required
              data-test-id={testId('expirationYear')}>{this.makeCCYears()}</select>
          </p>
          <p className={css.primaryPaymentCheckbox}>
            <input
              id="primaryPayment"
              type="checkbox"
              onChange={this.onHandleFormStateChange}
              data-field="isPrimary"
              value="true"
              data-test-id={testId('primaryPaymentCheckbox')} />
            <label htmlFor="primaryPayment" data-test-id={testId('makePrimaryPaymentLabel')}>Make this my primary payment</label>
          </p>
          <button
            className={css.savePayment}
            type="submit"
            disabled={isNewPaymentUpdating}
            data-test-id={testId('saveAddNewPayment')}>Save Payment</button>
          <button type="button" onClick={onCancelAddNewPayment} data-test-id={testId('cancelAddNewPayment')}>Cancel</button>
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  order: state.order
});

OrderCareAddPayment.contextTypes = {
  testId: PropTypes.func.isRequired
};

export default connect(mapStateToProps, {
  addNewPayment,
  fetchBillingAddresses,
  toggleAddNewBillingAddress,
  receiveAddNewPaymentError
})(OrderCareAddPayment);
